import { ExpandMore } from '@mui/icons-material';
import { Accordion, AccordionDetails, Typography, useEventCallback } from '@mui/material';
import { useFormik, useFormikContext } from 'formik';
import { DateTime } from 'luxon';
import { PmiButton } from 'shell/components/shared/button';
import { PracticeDetailVM } from 'practice/view-models/practice-detail';
import { Fragment, memo, useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { AccordionSummary } from 'shell/components/shared/summary-accordion';
import { __PRACTICE_API_URL__ } from 'shell/constants/environment';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import * as yup from 'yup';
import AccordionPositiveClosurePracticeForm, { CancellationPracticeVM } from './form';
import { TypeRegister } from 'practice/constants/type-register';
import { regexOnlyNumber } from 'shell/constants/regex';
import { ObjectShape } from 'yup/lib/object';
import ModalChangeParenthood, {
  FormModal,
} from 'practice/components/edit/form/shared/modal-change-parenthood';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { OtherFamiliarsServiceResponse } from 'shell/models/other-familiars';
import { HttpResponse } from 'shell/models/http-response';
import ModalWarningMessage from '../verification-messi/modal-warning';
import { blobToBase64 } from 'shell/utils/base64-to-blob';
import { ModalViewerPDF } from 'shell/components/shared/pdf-modal';
import ModalPrintSuccess from '../print-comunication/modal';

interface PositiveClosureProps {
  loadUpdateDate: () => Promise<PracticeDetailVM> | void;
}

const AccordionPositiveClosurePracticeComponent = ({
  loadUpdateDate,
}: PositiveClosureProps): React.ReactElement => {
  const [open, setOpen] = useState(true);

  const ref = useRef<HTMLDivElement | null>(null);

  const params = useParams();

  const navigate = useNavigate();

  const formContextPage = useFormikContext<PracticeDetailVM>();

  const updateData = useHttpClient(`${__PRACTICE_API_URL__}/chiusura-pratica/esito`, {
    options: { method: 'POST' },
  });

  // const checkNotificationControl = useHttpClient(
  //   `${__PRACTICE_API_URL__}/chiusura-pratica/controlli-notifica`,
  //   {
  //     options: { method: 'POST' },
  //   }
  // );

  const cancelNotifications = useHttpClient(
    `${__PRACTICE_API_URL__}/chiusura-pratica/cancella-notifica`,
    {
      options: { method: 'POST' },
    }
  );

  const printNotification = useHttpClient(
    `${__PRACTICE_API_URL__}/chiusura-pratica/stampa-notifica`,
    {
      options: { method: 'POST' },
    }
  );

  const onSubmit = useEventCallback((values: CancellationPracticeVM) => {
    const date = values.closureDate?.toFormat('yyyy-MM-dd') || '';
    const hours = values.closureTime?.toFormat('HH:mm:ss') || '';
    const timestampClosure =
      DateTime.fromFormat(`${date || ''} ${hours || ''}`, 'yyyy-MM-dd HH:mm:ss') || undefined;

    const data = {
      codiceEnte: params.codiceEnte || undefined,
      tipoRegistro: params.tipoRegistro || undefined,
      idAnnoPratica: params.idAnnoPratica ? Number.parseInt(params.idAnnoPratica, 10) : undefined,
      progressivoPratica: params.progressivoPratica
        ? Number.parseInt(params.progressivoPratica, 10)
        : undefined,
      tsChiusura: timestampClosure,
      motivoChiusura: values.closureReason || undefined,
      esito: '1', // 0 se chiusura negativa 1 chiusura positiva
      dataIscrizioneAireConsolato: values.dateSubscription
        ? values.dateSubscription?.toFormat('yyyy-MM-dd')
        : undefined,
      nrIscrizioneAireConsolato: values.subscriptionNumber
        ? Number.parseInt(values.subscriptionNumber, 10)
        : undefined,
    };

    (updateData({ data }) as ResponsePromise<void>).then(async () => {
      toast.success('La richiesta di chiusura positiva della pratica è stata registrata');

      // aggiornamento stato pratica
      formContextPage.setFieldValue('practice', {
        ...(formContextPage.values.practice || {}),
        codiceStatoPratica: '8',
        //tipoOperazione: '06',
      });

      // se cambia lo stato della pratica ricaricare dati
      const newData: PracticeDetailVM | void = await loadUpdateDate();

      if (newData) {
        formContextPage.setValues({
          ...(formContextPage.values || {}),
          ...newData,
        });
      }
      navigate({
        pathname: generatePath(
          '/pratiche/ricerca/dettaglio/:codiceEnte/:tipoRegistro/:idAnnoPratica/:progressivoPratica/:idDbLocaleRichiedente/:idAnprIntestatario/:tipoPratica',
          {
            codiceEnte: params.codiceEnte,
            tipoRegistro: params.tipoRegistro,
            idAnnoPratica: params.idAnnoPratica?.toString(),
            progressivoPratica: params.progressivoPratica?.toString(),
            idDbLocaleRichiedente: params.idDbLocaleRichiedente?.toString() || '',
            idAnprIntestatario: params.idAnprIntestatario?.toString() || '',
            tipoPratica: params.tipoPratica,
          }
        ),
      });
    });
  });

  const [openModalParenthood, setOpenModalParenthood] = useState(false);

  const [warningModal, setWarningModal] = useState<{ open: boolean; warnings?: string }>({
    open: false,
  });

  const [pdfModal, setPdfModal] = useState<{ open: boolean; data?: string; name?: string }>({
    data: '',
    open: false,
    name: '',
  });

  const [modalPrintSuccess, setModalPrintSuccess] = useState<{
    open: boolean;
    data?: string;
  }>({
    open: false,
  });

  useEffect(() => {
    ref.current?.scrollIntoView();
  }, []);

  const validationSchema = useMemo(() => {
    const shape: ObjectShape = {
      closureDate: yup
        .date()
        .typeError('Data non valida')
        .nullable()
        .required('Il campo è obbligatorio'),
      closureTime: yup
        .date()
        .typeError('Data non valida')
        .nullable()
        .required('Il campo è obbligatorio'),
    };

    if (params.tipoRegistro === TypeRegister.Emigration) {
      shape.subscriptionNumber = yup.string().nullable().matches(regexOnlyNumber, 'Campo numerico');
    }

    return yup.object().shape(shape);
  }, [params.tipoRegistro]);

  const formContext = useFormik<CancellationPracticeVM>({
    initialValues: {
      closureDate: DateTime.now(),
      closureTime: DateTime.now(),
      closureReason: '',
      dateSubscription: formContextPage.values.subscriptionAire?.date,
      subscriptionNumber: formContextPage.values.subscriptionAire?.number,
    },
    validationSchema,
    onSubmit,
  });

  // fetch to get other familiars for change relationship
  const [otherFamiliars] = useHttpFetch<HttpResponse<OtherFamiliarsServiceResponse>, FormModal>(
    `${__PRACTICE_API_URL__}/persona-parentela/list/${params.codiceEnte}-${params.idAnnoPratica}-${params.tipoRegistro}-${params.progressivoPratica}`,
    {
      onBootstrap: true,
      transformer: (response) => {
        return {
          otherFamiliars:
            response.data.payload?.altriFamiliari?.map((element) => {
              const isMinorenne = element.dataNascita
                ? DateTime.now().diff(DateTime.fromISO(element.dataNascita), 'years').years < 18
                : false;

              const parenthoodValue =
                !isMinorenne &&
                response.data.payload?.altriFamiliari?.length === 1 &&
                params.tipoRegistro !== TypeRegister.Unavailable
                  ? '1'
                  : element.codAnprParentela?.toString() || '';
              return {
                fiscalCode: element.codFiscale || '',
                name: element.nome || '',
                surname: element.cognome || '',
                parenthoodValue,
                isMinorenne,
                element,
              };
            }) || [],
          newFamiliars:
            response.data.payload?.nuoviFamiliari?.map((element) => {
              const parenthoodValue =
                response.data.payload?.nuoviFamiliari?.length === 1 &&
                params.tipoRegistro !== TypeRegister.Unavailable
                  ? '1'
                  : element.codAnprParentela?.toString() || '';

              return {
                fiscalCode: element.codFiscale || '',
                name: element.nome || '',
                surname: element.cognome || '',
                parenthoodValue,
                elementFamilyPractice: element,
              };
            }) || [],
        };
      },
      options: {
        errorCatching: true,
        method: 'GET',
        loading: true,
      },
    }
  );

  const otherFamiliarsVM = (otherFamiliars.data as FormModal) || {};

  const onCancelModalParenthood = useEventCallback((isConfirm: boolean) => {
    if (isConfirm) {
      formContext.handleSubmit();
    }
    setOpenModalParenthood(false);
  });

  // const controlNotificationData = useCallback(
  //   (values: CancellationPracticeVM) => {
  //     const date = values.closureDate?.toFormat('yyyy-MM-dd') || '';
  //     const hours = values.closureTime?.toFormat('HH:mm:ss') || '';
  //     const timestampClosure =
  //       DateTime.fromFormat(`${date || ''} ${hours || ''}`, 'yyyy-MM-dd HH:mm:ss') || undefined;

  //     (
  //       checkNotificationControl({
  //         data: {
  //           codiceEnte: params.codiceEnte || undefined,
  //           tipoRegistro: params.tipoRegistro || undefined,
  //           idAnnoPratica: params.idAnnoPratica
  //             ? Number.parseInt(params.idAnnoPratica, 10)
  //             : undefined,
  //           progressivoPratica: params.progressivoPratica
  //             ? Number.parseInt(params.progressivoPratica, 10)
  //             : undefined,
  //           tsChiusura: timestampClosure,
  //           esito: '1', // 0 se chiusura negativa 1 chiusura positiva
  //         },
  //       }) as ResponsePromise<HttpResponse<{ showMessage: boolean; messaggio: string }>>
  //     ).then((response) => {
  //       if (response?.data.payload.showMessage) {
  //         setWarningModal({ open: true, warnings: response?.data.payload.messaggio });
  //       } else {
  //         formContext.handleSubmit();
  //       }
  //     });
  //   },
  //   [checkNotificationControl]
  // );

  const cancelNotification = useCallback(
    (values: CancellationPracticeVM) => {
      const date = values.closureDate?.toFormat('yyyy-MM-dd') || '';
      const hours = values.closureTime?.toFormat('HH:mm:ss') || '';
      const timestampClosure =
        DateTime.fromFormat(`${date || ''} ${hours || ''}`, 'yyyy-MM-dd HH:mm:ss') || undefined;

      (
        cancelNotifications({
          data: {
            codiceEnte: params.codiceEnte || undefined,
            tipoRegistro: params.tipoRegistro || undefined,
            idAnnoPratica: params.idAnnoPratica
              ? Number.parseInt(params.idAnnoPratica, 10)
              : undefined,
            progressivoPratica: params.progressivoPratica
              ? Number.parseInt(params.progressivoPratica, 10)
              : undefined,
            tsChiusura: timestampClosure,
            esito: '1', // 0 se chiusura negativa 1 chiusura positiva
          },
        }) as ResponsePromise<void>
      ).then(() => {
        formContext.handleSubmit();
      });
    },
    [cancelNotifications]
  );

  const printNotifications = useCallback(
    (values: CancellationPracticeVM) => {
      const date = values.closureDate?.toFormat('yyyy-MM-dd') || '';
      const hours = values.closureTime?.toFormat('HH:mm:ss') || '';
      const timestampClosure =
        DateTime.fromFormat(`${date || ''} ${hours || ''}`, 'yyyy-MM-dd HH:mm:ss') || undefined;

      (
        printNotification({
          responseType: 'blob',
          data: {
            codiceEnte: params.codiceEnte || undefined,
            tipoRegistro: params.tipoRegistro || undefined,
            idAnnoPratica: params.idAnnoPratica
              ? Number.parseInt(params.idAnnoPratica, 10)
              : undefined,
            progressivoPratica: params.progressivoPratica
              ? Number.parseInt(params.progressivoPratica, 10)
              : undefined,
            tsChiusura: timestampClosure,
            esito: '1', // 0 se chiusura negativa 1 chiusura positiva
          },
        }) as ResponsePromise<void>
      ).then((response) => {
        if (response?.data) {
          const blob = new Blob([(response.data as string) || ''], { type: 'application/pdf' });

          setWarningModal({
            open: false,
          });

          blobToBase64(blob).then((base64) => {
            setPdfModal({
              data: base64.split(',')[1] as string,
              name: 'Stampa comunicazione',
              open: true,
            });
          });
        }
      });
    },
    [printNotification]
  );

  return (
    <Fragment>
      {openModalParenthood ? (
        <ModalChangeParenthood
          open={openModalParenthood}
          onClose={onCancelModalParenthood}
          otherFamiliars={otherFamiliarsVM?.otherFamiliars}
          newFamiliars={otherFamiliarsVM?.newFamiliars}
          typeRegister={params.tipoRegistro}
          yearId={params.idAnnoPratica}
          progressive={params.progressivoPratica}
          codeEnte={params.codiceEnte}
          fromPerson={formContextPage.values.practice?.tipoIniziativa}
        />
      ) : undefined}
      {warningModal.open ? (
        <ModalWarningMessage
          onYes={() => {
            printNotifications(formContext.values);
          }}
          onNo={() => {
            cancelNotification(formContext.values);
          }}
          open={warningModal.open}
          message={warningModal.warnings || ''}
          hasAnnulla
          onCloseAnnulla={() => {
            setWarningModal({ open: false });
          }}
        />
      ) : undefined}
      {pdfModal.open ? (
        <ModalViewerPDF
          open={pdfModal.open}
          data={pdfModal.data || ''}
          name={pdfModal.name || 'Stampa Notifica'}
          title={pdfModal.name || 'Stampa Notifica'}
          onClose={() => {
            setModalPrintSuccess((oldState) => ({
              ...oldState,
              data: pdfModal.data,
              open: true,
            }));
            setPdfModal({ data: '', open: false, name: '' });
          }}
        />
      ) : undefined}
      {modalPrintSuccess.open ? (
        <ModalPrintSuccess
          open={modalPrintSuccess.open}
          onYes={() => {
            setPdfModal({ open: false });
            formContext.handleSubmit();
          }}
          onClose={() => {
            setPdfModal({ name: '', data: modalPrintSuccess.data || '', open: true });
            setModalPrintSuccess({ open: false });
          }}
        />
      ) : undefined}
      <Accordion
        expanded={open}
        onChange={() => {
          setOpen(!open);
        }}
        ref={ref}
      >
        <AccordionSummary
          aria-controls="panel-positive-closure-practice-content"
          id="panel-positive-closure-practice-header"
          expandIcon={<ExpandMore />}
        >
          <Typography>Chiusura Pratica Positiva</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <AccordionPositiveClosurePracticeForm
            values={formContext.values}
            onChange={formContext.setFieldValue}
            errors={formContext.errors}
            touched={formContext.touched}
            isEmigr={params.tipoRegistro === TypeRegister.Emigration}
          />
          <div className="pmi-align-right pmi-pt-2">
            <PmiButton
              color="cdmBrandThird"
              variant="outlined"
              onClick={() => {
                formContext.validateForm(formContext.values).then((errors) => {
                  if (Object.values(errors).length > 0) {
                    return;
                  }
                  if (
                    (params.tipoRegistro === TypeRegister.Unavailable ||
                      params.tipoRegistro === TypeRegister.Emigration) &&
                    (otherFamiliarsVM.otherFamiliars.length > 0 ||
                      otherFamiliarsVM.newFamiliars.length > 0)
                  ) {
                    setOpenModalParenthood(true);
                  }
                  // else if (params.tipoRegistro === TypeRegister.Immigration) {
                  //   controlNotificationData(formContext.values);
                  //}
                  else {
                    formContext.handleSubmit();
                  }
                });
              }}
              id="confirm"
            >
              Conferma
            </PmiButton>
          </div>
        </AccordionDetails>
      </Accordion>
    </Fragment>
  );
};

AccordionPositiveClosurePracticeComponent.displayName =
  'Accordion Positive Closure Practice component';

const AccordionPositiveClosurePractice = memo(AccordionPositiveClosurePracticeComponent);

export default AccordionPositiveClosurePractice;
